import { motion } from "framer-motion";
import { Instagram, Calendar, Layers, Star, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { DreamMagnet } from "@/components/DreamMagnet";

const ease = [0.22, 1, 0.36, 1];

const LINKS = [
    {
        label: "Services & pricing",
        sub: "From £149 · launch offer",
        href: "/#pricing",
        icon: Layers,
        testid: "start-link-pricing",
    },
    {
        label: "Why I'm the UK's first",
        sub: "Data analyst for people & business",
        href: "/#why",
        icon: Star,
        testid: "start-link-why",
    },
    {
        label: "Follow on Instagram",
        sub: "@the_rawmath",
        href: "https://instagram.com/the_rawmath",
        icon: Instagram,
        external: true,
        testid: "start-link-instagram",
    },
];

export default function Start() {
    return (
        <div
            data-testid="start-page"
            className="min-h-screen bg-[#070A13] text-[#FDFBF7] grain"
        >
            {/* Top brand bar */}
            <motion.header
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease }}
                className="max-w-xl mx-auto px-6 pt-12 pb-8 flex flex-col items-center text-center"
            >
                <Logo className="w-14 h-14 mb-5" dark />
                <p className="font-display text-3xl tracking-tight">
                    The Raw Math
                </p>
                <p className="overline text-[#C5A059] mt-3">
                    UK&apos;s first — raw math for people & business
                </p>
                <h1 className="font-display text-3xl sm:text-4xl tracking-tighter leading-[1.05] mt-8">
                    See exactly{" "}
                    <span className="italic text-[#C5A059]">when</span> you&apos;ll
                    reach your dream — and how to get there sooner.
                </h1>
                <p className="font-body text-white/55 mt-4">
                    Free 60-second preview. Real numbers, no jargon.
                </p>
            </motion.header>

            {/* The lead magnet — the whole point of the bio link */}
            <div className="border-y border-white/10">
                <DreamMagnet />
            </div>

            {/* Link stack */}
            <div className="max-w-xl mx-auto px-6 py-12">
                <p className="overline text-white/40 text-center mb-6">
                    — Or explore
                </p>
                <div className="flex flex-col gap-4">
                    <motion.a
                        data-testid="start-link-call"
                        href="/#pricing"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, ease }}
                        className="group flex items-center gap-4 border border-[#C5A059]/40 bg-[#C5A059]/[0.08] hover:bg-[#C5A059]/[0.15] rounded-xl px-5 py-4 transition-colors duration-300 text-left"
                    >
                        <span className="w-10 h-10 rounded-full bg-[#C5A059] flex items-center justify-center text-[#070A13] shrink-0">
                            <Calendar size={18} />
                        </span>
                        <span className="flex-1">
                            <span className="block font-body font-semibold">
                                Book your Signal call
                            </span>
                            <span className="block font-mono text-[11px] text-white/50">
                                Secure your slot, 1:1
                            </span>
                        </span>
                        <ArrowUpRight
                            size={18}
                            className="text-[#C5A059] group-hover:rotate-45 transition-transform duration-300 shrink-0"
                        />
                    </motion.a>
                    {LINKS.map((l, i) => {
                        const Icon = l.icon;
                        return (
                            <motion.a
                                key={l.href}
                                data-testid={l.testid}
                                href={l.href}
                                target={l.external ? "_blank" : undefined}
                                rel={l.external ? "noreferrer" : undefined}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.06,
                                    ease,
                                }}
                                className="group flex items-center gap-4 border border-white/15 hover:border-[#C5A059] hover:bg-white/[0.04] rounded-xl px-5 py-4 transition-colors duration-300"
                            >
                                <span className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[#C5A059] group-hover:text-[#C5A059] transition-colors shrink-0">
                                    <Icon size={18} />
                                </span>
                                <span className="flex-1">
                                    <span className="block font-body font-semibold">
                                        {l.label}
                                    </span>
                                    <span className="block font-mono text-[11px] text-white/45">
                                        {l.sub}
                                    </span>
                                </span>
                                <ArrowUpRight
                                    size={18}
                                    className="text-white/30 group-hover:text-[#C5A059] group-hover:rotate-45 transition-all duration-300 shrink-0"
                                />
                            </motion.a>
                        );
                    })}
                </div>

                <p className="font-mono text-[11px] text-white/25 text-center mt-10">
                    © {new Date().getFullYear()} The Raw Math
                </p>
            </div>
        </div>
    );
}
